import { create } from 'zustand'
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { DEFAULT_TEMPLATES, type EmailTemplateData } from '../utils/emailTemplates'

interface EmailTemplatesState {
  overrides: Record<string, EmailTemplateData | null>
  loading: Record<string, boolean>
  saving: boolean
  fetchTemplate: (id: string) => Promise<EmailTemplateData>
  saveTemplate: (id: string, data: EmailTemplateData) => Promise<void>
  resetTemplate: (id: string) => Promise<void>
}

export const useEmailTemplatesStore = create<EmailTemplatesState>((set) => ({
  overrides: {},
  loading: {},
  saving: false,

  async fetchTemplate(id) {
    set((s) => ({ loading: { ...s.loading, [id]: true } }))
    try {
      const snap = await getDoc(doc(db, 'email_templates', id))
      if (snap.exists()) {
        const data = snap.data()
        const defaults = DEFAULT_TEMPLATES[id]
        const merged: EmailTemplateData = {
          subject: (data.subject as string | undefined) ?? defaults.subject,
          title: (data.title as string | undefined) ?? defaults.title,
          body: (data.body as string | undefined) ?? defaults.body,
          ctaText: (data.ctaText as string | null | undefined) ?? defaults.ctaText,
        }
        set((s) => ({ overrides: { ...s.overrides, [id]: merged } }))
        return merged
      }
    } catch (err) {
      console.warn('[emailTemplates.fetchTemplate] using defaults:', err)
    } finally {
      set((s) => ({ loading: { ...s.loading, [id]: false } }))
    }
    return DEFAULT_TEMPLATES[id]
  },

  async saveTemplate(id, data) {
    set({ saving: true })
    try {
      await setDoc(doc(db, 'email_templates', id), {
        subject: data.subject,
        title: data.title,
        body: data.body,
        ctaText: data.ctaText ?? null,
      })
      set((s) => ({ overrides: { ...s.overrides, [id]: { ...data } } }))
    } finally {
      set({ saving: false })
    }
  },

  async resetTemplate(id) {
    set({ saving: true })
    try {
      await deleteDoc(doc(db, 'email_templates', id))
      set((s) => ({ overrides: { ...s.overrides, [id]: null } }))
    } finally {
      set({ saving: false })
    }
  },
}))
